import React, { useState, useEffect, FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';
import { graphql, createFragmentContainer } from 'react-relay';
import idx from 'idx';
import Calendar from 'dibs-vg/dist/react/calendar';
import { trackEvent } from 'dibs-tracking';

import { PdpSharedBlurb } from '../PdpSharedBlurbs/PdpSharedBlurb/PdpSharedBlurb';
import { shouldShowCoachmark } from '../PdpSharedNotifications/PdpSharedNotificationTradeRequestAppointmentTooltip/helpers';
import PdpRequestAppointmentModalLoader from '../PdpRequestAppointmentModal/PdpRequestAppointmentModalLoader';

import styles from './PdpSharedBlurbTradeRequestAppointment.scss';

import { PdpSharedBlurbTradeRequestAppointment_viewer as ViewerType } from './__generated__/PdpSharedBlurbTradeRequestAppointment_viewer.graphql';
import { PdpSharedBlurbTradeRequestAppointment_user as UserType } from './__generated__/PdpSharedBlurbTradeRequestAppointment_user.graphql';
import { PdpRespSidebarBlurbs_item$ref as ItemTypeRef } from '../PdpRespSidebarBlurbs/__generated__/PdpRespSidebarBlurbs_item.graphql';

type PdpSharedBlurbTradeRequestAppointmentProps = {
    user: UserType;
    viewer: ViewerType;
    item: ItemTypeRef;
};

const showBadge = Date.now() < new Date('2019-10-29').getTime();

const PdpSharedRequestAppointmentCoachmark = React.lazy(() =>
    import(
        /* webpackChunkName: "PdpSharedRequestAppointmentCoachmark" */ './PdpSharedTradeRequestAppointmentCoachmark'
    )
);

const PdpSharedBlurbTradeRequestAppointment: FunctionComponent<
    PdpSharedBlurbTradeRequestAppointmentProps
> = ({ user, viewer, item }) => {
    const [showCoachmark, setShowCoachmark] = useState(shouldShowCoachmark());
    const [isModalOpen, setIsModalOpen] = useState(false);

    const tradeFirmId = idx(user, o => o.tradeFirm.serviceId) || '';
    const whitelist = idx(viewer, o => o.anchorLists[0].members) || [];
    const firmIsWhitelisted = !!whitelist.find(
        member => idx(member, o => o.anchor.sourceId) === tradeFirmId
    );

    const isDisabled = idx(viewer, o => o.disableRequestAppointment);
    const shouldShowForVertical = idx(item, o => o.isArt) || idx(item, o => o.isFurniture);
    const shouldRender = !isDisabled && firmIsWhitelisted && shouldShowForVertical;

    useEffect(() => {
        if (shouldRender) {
            trackEvent({
                category: 'trade features',
                action: 'request appointment',
                label: 'request appointment CTA displayed',
            });
        }
    }, [shouldRender]);

    if (!shouldRender) {
        return null;
    }

    return (
        <React.Fragment>
            <PdpSharedBlurb IconComponent={Calendar}>
                <div className={styles.blurbHeaderWrapper}>
                    <FormattedMessage
                        id="pdp.PdpSharedBlurbTradeRequestAppointment.title"
                        defaultMessage="See this item in person"
                    />
                    {showBadge ? (
                        <div className={styles.newBadge}>
                            <FormattedMessage
                                id="pdp.PdpSharedBlurbTradeRequestAppointment.newBadge"
                                defaultMessage="new"
                            />
                        </div>
                    ) : null}
                    {showCoachmark && (
                        <React.Suspense fallback="">
                            <PdpSharedRequestAppointmentCoachmark
                                onTooltipDismissed={() => setShowCoachmark(false)}
                            />
                        </React.Suspense>
                    )}
                </div>
                <div>
                    <a
                        onClick={() => {
                            trackEvent({
                                category: 'trade features',
                                action: 'request appointment',
                                label: 'request appointment link clicked',
                            });
                            setIsModalOpen(true);
                        }}
                    >
                        <FormattedMessage
                            id="pdp.PdpSharedBlurbTradeRequestAppointment.link"
                            defaultMessage="Request appointment"
                        />
                    </a>
                </div>
                <PdpRequestAppointmentModalLoader
                    isModalOpen={isModalOpen}
                    closeModal={() => setIsModalOpen(false)}
                    item={item}
                    user={user}
                />
            </PdpSharedBlurb>
        </React.Fragment>
    );
};

export default createFragmentContainer(PdpSharedBlurbTradeRequestAppointment, {
    viewer: graphql`
        fragment PdpSharedBlurbTradeRequestAppointment_viewer on Viewer {
            disableRequestAppointment: isEnabled(key: "disablePdpRequestAppointment")
            anchorLists(listNames: ["REQUEST_APPOINTMENT_MVP"]) {
                members {
                    anchor {
                        sourceId
                    }
                }
            }
        }
    `,
    user: graphql`
        fragment PdpSharedBlurbTradeRequestAppointment_user on User {
            tradeFirm {
                serviceId
            }
            ...PdpRequestAppointmentModalLoader_user
        }
    `,
    item: graphql`
        fragment PdpSharedBlurbTradeRequestAppointment_item on Item {
            ...PdpRequestAppointmentModalLoader_item
            isArt
            isFurniture
        }
    `,
});
